// src/firebase/services.js
import { db, storage } from "./config";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

// ─── PRODUCTS ────────────────────────────────────────────────────────────────

export async function getProducts(filters = {}) {
  const productsRef = collection(db, "products");
  const constraints = [orderBy("order", "asc")];
  if (filters.niche) constraints.unshift(where("niches", "array-contains", filters.niche));
  if (filters.active !== undefined) constraints.unshift(where("active", "==", filters.active));

  const q = query(productsRef, ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getProductBySlug(slug) {
  const q = query(collection(db, "products"), where("slug", "==", slug));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
}

export async function addProduct(productData) {
  return await addDoc(collection(db, "products"), {
    ...productData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateProduct(id, productData) {
  const ref = doc(db, "products", id);
  return await updateDoc(ref, { ...productData, updatedAt: serverTimestamp() });
}

export async function deleteProduct(id) {
  return await deleteDoc(doc(db, "products", id));
}

// ─── NICHES ──────────────────────────────────────────────────────────────────

export async function getNiches() {
  const q = query(collection(db, "niches"), orderBy("order", "asc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addNiche(nicheData) {
  return await addDoc(collection(db, "niches"), {
    ...nicheData,
    createdAt: serverTimestamp(),
  });
}

export async function updateNiche(id, nicheData) {
  return await updateDoc(doc(db, "niches", id), nicheData);
}

// ─── ENQUIRIES ───────────────────────────────────────────────────────────────

export async function submitEnquiry(enquiryData) {
  return await addDoc(collection(db, "enquiries"), {
    ...enquiryData,
    status: "new",
    createdAt: serverTimestamp(),
  });
}

export async function getEnquiries() {
  const q = query(collection(db, "enquiries"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ─── IMAGE UPLOAD ─────────────────────────────────────────────────────────────

export async function uploadProductImage(file, productSlug) {
  const storageRef = ref(storage, `products/${productSlug}/${file.name}`);
  const snapshot = await uploadBytes(storageRef, file);
  return await getDownloadURL(snapshot.ref);
}

export async function deleteProductImage(imageUrl) {
  const storageRef = ref(storage, imageUrl);
  return await deleteObject(storageRef);
}

// ─── SITE CONFIG ─────────────────────────────────────────────────────────────

export async function getSiteConfig() {
  const docRef = doc(db, "config", "site");
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : null;
}

export async function updateSiteConfig(data) {
  const docRef = doc(db, "config", "site");
  return await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });
}
